import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { createStore, delMany, get, keys, set } from 'idb-keyval';

import { AuthService } from '../../../../shared/auth/auth.service';
import { FotoVistoria } from '../models/item-vistoria-preenchimento';
import { Vistoria } from '../models/vistoria';

/** Muda quando o formato do `Vistoria` muda de um jeito que um rascunho antigo não serve mais. */
const VERSAO = 1;

interface RascunhoGravado {
  versao: number;
  data: string;
  dataModificacaoServidor: string | null;
  vistoria: string;
}

export interface RascunhoVistoria {
  data: Date;
  dataModificacaoServidor: string | null;
  /** Já com as datas de volta como `Date` e o `Blob` de volta em cada foto pendente. */
  vistoria: Vistoria;
}

/**
 * Rascunho da vistoria em edição no IndexedDB do aparelho: sobrevive ao Android encerrar o Chrome (ex: ao
 * abrir a câmera) e ao recarregar a página. Fornecido pela tela `realizar-vistoria` (uma instância por tela),
 * só pra vistoria já salva. O JSON e cada foto pendente ficam em chaves separadas: a foto é gravada uma vez
 * só e as gravações seguintes levam só o texto.
 *
 * O IndexedDB já é separado por origem (subdomínio = tenant); o login na chave separa as contas.
 */
@Injectable()
export class RascunhoVistoriaService {

  /** Pedido de gravação imediata, vindo de quem não tem a vistoria (ex: "Tirar foto", nos itens). */
  public readonly gravarAgora$ = new Subject<void>();

  private readonly store = createStore('gma-rascunho-vistoria', 'rascunhos');
  private prefixo = '';
  private ultimoJson: string | null = null;
  private fotosGravadas = new Set<string>();
  // Pode haver rascunho gravado: a tela pede pra apagar a cada interação sem alteração, e isto evita
  // varrer as chaves toda vez. Começa true porque pode ter ficado um de antes de recarregar a página.
  private podeExistir = true;
  // As operações em fila: um apagar depois do salvar não pode ser ultrapassado por uma gravação anterior.
  private fila: Promise<unknown> = Promise.resolve();

  constructor(private authService: AuthService) { }

  public iniciar(vistoriaId: number): void {
    this.prefixo = `${this.authService.getLogin()}:${vistoriaId}`;
    this.ultimoJson = null;
    this.fotosGravadas.clear();
    this.podeExistir = true;
    // O navegador decide se atende; sem isso, pode apagar o rascunho quando faltar espaço no aparelho.
    navigator.storage?.persist?.().catch(() => { });
  }

  public solicitarGravacao(): void {
    this.gravarAgora$.next();
  }

  /** Rascunho da vistoria iniciada, ou null se não há (ou é de um formato antigo, que é apagado). */
  public carregar(): Promise<RascunhoVistoria | null> {
    return this.enfileirar(async () => {
      const gravado = await get<RascunhoGravado>(this.chaveRascunho(), this.store);
      if (!gravado) return null;
      if (gravado.versao !== VERSAO) {
        await this.apagarTudo();
        return null;
      }

      const vistoria: Vistoria = JSON.parse(gravado.vistoria);
      vistoria.data = vistoria.data ? new Date(vistoria.data) : null;
      for (const comodo of vistoria.comodos)
        for (const item of comodo.itens)
          for (const atributo of item.atributos)
            atributo.fotos = await this.restaurarFotos(atributo.fotos);

      this.ultimoJson = gravado.vistoria;
      return { data: new Date(gravado.data), dataModificacaoServidor: gravado.dataModificacaoServidor, vistoria };
    });
  }

  /** `json` é o `JSON.stringify` da vistoria, que a tela já calculou pra comparar com o salvo. */
  public gravar(vistoria: Vistoria, json: string): Promise<void> {
    return this.enfileirar(async () => {
      if (json === this.ultimoJson) return;
      this.podeExistir = true;

      const pendentes = new Map<string, Blob>();
      for (const comodo of vistoria.comodos)
        for (const item of comodo.itens)
          for (const atributo of item.atributos)
            for (const foto of atributo.fotos)
              if (foto.arquivo) pendentes.set(foto.chave, foto.arquivo);

      for (const [chave, blob] of pendentes) {
        if (this.fotosGravadas.has(chave)) continue;
        await set(this.chaveFoto(chave), blob, this.store);
        this.fotosGravadas.add(chave);
      }

      const removidas = [...this.fotosGravadas].filter(chave => !pendentes.has(chave));
      if (removidas.length) {
        await delMany(removidas.map(chave => this.chaveFoto(chave)), this.store);
        removidas.forEach(chave => this.fotosGravadas.delete(chave));
      }

      const rascunho: RascunhoGravado = {
        versao: VERSAO,
        data: new Date().toISOString(),
        dataModificacaoServidor: vistoria.dataModificacao,
        vistoria: json,
      };
      await set(this.chaveRascunho(), rascunho, this.store);
      this.ultimoJson = json;
    });
  }

  public apagar(): Promise<void> {
    return this.enfileirar(() => this.apagarTudo());
  }

  private async apagarTudo(): Promise<void> {
    if (!this.prefixo || !this.podeExistir) return;
    const prefixoFotos = this.chaveFoto('');
    const chaves = (await keys<string>(this.store)).filter(c => c === this.chaveRascunho() || c.startsWith(prefixoFotos));
    if (chaves.length) await delMany(chaves, this.store);
    this.ultimoJson = null;
    this.fotosGravadas.clear();
    this.podeExistir = false;
  }

  /** Foto pendente cujo Blob sumiu (o navegador apagou parte dos dados) sai da vistoria. */
  private async restaurarFotos(fotos: any[]): Promise<FotoVistoria[]> {
    const restauradas: FotoVistoria[] = [];
    for (const foto of fotos) {
      const restaurada: FotoVistoria = { chave: foto.chave, dataCaptura: new Date(foto.dataCaptura) };
      // No JSON o Blob vira {}: a chave "arquivo" só existe nas fotos que estavam pendentes.
      if (!('arquivo' in foto)) {
        restauradas.push(restaurada);
        continue;
      }

      const arquivo = await get<Blob>(this.chaveFoto(foto.chave), this.store);
      if (!arquivo) continue;
      restauradas.push({ ...restaurada, arquivo });
      this.fotosGravadas.add(foto.chave);
    }
    return restauradas;
  }

  private enfileirar<T>(operacao: () => Promise<T>): Promise<T> {
    const resultado = this.fila.then(operacao);
    this.fila = resultado.catch(() => { });
    return resultado;
  }

  private chaveRascunho(): string {
    return `${this.prefixo}:vistoria`;
  }

  private chaveFoto(chave: string): string {
    return `${this.prefixo}:foto:${chave}`;
  }
}
